import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import FullDocumentationView from "./FullDocumentationView"
import { documentationApi } from "../../../services/api"
import Button from "../../../components/ui/Button"

const FullDocumentationPage = () => {
  const { docId } = useParams()
  const navigate = useNavigate()
  const [doc, setDoc] = useState(null)
  const [loading, setLoading] = useState(true)
  const [regenLoading, setRegenLoading] = useState(false)
  const [downloading, setDownloading] = useState("")
  const [error, setError] = useState("")

  useEffect(() => {
    if (!docId) return
    let mounted = true
    setLoading(true)
    setError("")
    documentationApi.get(docId)
      .then((res) => {
        if (!mounted) return
        setDoc(res?.data?.data || null)
      })
      .catch((e) => {
        if (!mounted) return
        setError(e?.response?.data?.message || e.message || "Failed to load documentation")
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })
    return () => { mounted = false }
  }, [docId])

  const handleRegenerateAll = async () => {
    if (!doc?._id) return
    const confirmed = window.confirm("Regenerate the entire document now? This will replace all current section content.")
    if (!confirmed) return
    const noteInput = window.prompt("Optional note for full-document regeneration (leave empty to skip):", "")
    const note = typeof noteInput === "string" ? noteInput.trim() : ""
    setRegenLoading(true)
    setError("")
    try {
      const res = await documentationApi.regenerate(doc._id, note ? { note } : {})
      if (res?.data?.data) setDoc(res.data.data)
    } catch (e) {
      if (e?.response?.status === 402) {
        setError("Regeneration limit reached. Purchase more to regenerate.")
      } else {
        setError(e?.response?.data?.message || "Regenerate failed")
      }
    } finally {
      setRegenLoading(false)
    }
  }

  const fileBaseName = String(doc?.project?.title || "documentation")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "documentation"

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleExportPdf = async () => {
    if (!doc?._id) return
    setDownloading("pdf")
    setError("")
    try {
      const res = await documentationApi.exportPdf(doc._id)
      const blob = new Blob([res.data], { type: "application/pdf" })
      downloadBlob(blob, `${fileBaseName}.pdf`)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to export PDF")
    } finally {
      setDownloading("")
    }
  }

  const handleExportDocx = async () => {
    if (!doc?._id) return
    setDownloading("docx")
    setError("")
    try {
      const res = await documentationApi.exportDocx(doc._id)
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      })
      downloadBlob(blob, `${fileBaseName}.docx`)
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to export Word")
    } finally {
      setDownloading("")
    }
  }

  if (loading) {
    return <div className="p-4 text-slate-600 animate-soft-pulse">Loading full documentation...</div>
  }

  if (!doc) {
    return (
      <div className="p-4">
        <Button size="sm" variant="outline" onClick={() => navigate("/dashboard/project")}>Back</Button>
        <p className="mt-2 text-slate-600">{error || "Documentation not found."}</p>
      </div>
    )
  }

  return (
    <div>
      {error ? <p className="mb-3 text-sm text-error">{error}</p> : null}
      <FullDocumentationView
        doc={doc}
        onBack={() => navigate("/dashboard/project")}
        onOpenChapter={(ch) => navigate(`/dashboard/project/chapter/${doc._id}/${ch._id}`)}
        onRegenerateAll={handleRegenerateAll}
        regenLoading={regenLoading}
        onExportPdf={handleExportPdf}
        onExportDocx={handleExportDocx}
        downloading={downloading}
      />
    </div>
  )
}

export default FullDocumentationPage
